import { useEffect, useRef } from 'react';
import { useAolStore } from '../context/AolContext';
import { AolSectionSignalRDTO } from '../types/Dto/AolSectionSignalRDTO';
import { NoonReportStatusEnum } from '../types/ViewModel/AolEnums';
import { AolDataItem, AolGroup } from '../types/ViewModel/AolItem';
import { NoonReportFuelRobSection, ValidatedNoonReport } from '../types/ViewModel/ValidatedNoonReport';
import { AolSectionDataUpdateDto } from '../types/Dto/AolSectionDataUpdateDto';
import { getAolGroupStatus } from '../utils/aolFieldsHelper';
import { aolFilterNoonReportStatus, GetSection, setSection } from '../utils/aolSectionHelper';
import { HubConnection } from '@microsoft/signalr';
import { useToast } from './useToast';
import { useVesselStore } from '../context/VesselContext';
import { makeAolDataItemList, makeAolGroupList } from '../factory/Aol/AolDataItemFactory';
import { getAolGroupDate } from '../utils/aol/aolDateUtils';
import useUpdateMissingTasksIcon from './useUpdateMissingTasksIcon';
import { AppFeatureEnum } from '../enums/appFeatureEnum';
import { isDateSame, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import useAppFeature from './useAppFeature';
import { AolPageEnum } from 'src/enums/aol/AolPageEnum';
import { useBunkerContext } from 'src/context/BunkerContext';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { NoonReportMetadataFactory } from '../factory/ValidatedNoonReportFactory';

const noonReportMetadataFactory = new NoonReportMetadataFactory();

export const useAolUpdateHandler = () => {
    const {
        setAolGroups,
        aolGroups,
        setAolDataItems,
        selectedAolGroup,
        setAolGroupStatusMessage,
        setSelectedAolDataItem,
        setPreviousAolGroup,
        setIsUpdatingAolGroupStatus,
        selectedAolDataItem,
        setSaveAolEvent,
        setAolSyncEvent,
        aolDataItems,
        noonReports,
        aolItems,
        setNoonReports,
        activeAolPage,
        aolFilter,
    } = useAolStore();
    const { selectedBunkeringDate } = useBunkerContext();
    const { selectedVessel } = useVesselStore();
    const { loadedMasterStowagePlan } = useStowagePlanStore();
    const { toastSuccess } = useToast();
    const { updateAolMissingTasks } = useUpdateMissingTasksIcon();
    const { isFeatureEnabled } = useAppFeature();

    // Refs to always access latest state, preventing stale closures during burst processing
    const aolGroupsRef = useRef(aolGroups);
    const aolDataItemsRef = useRef(aolDataItems);

    useEffect(() => { aolGroupsRef.current = aolGroups; }, [aolGroups]);
    useEffect(() => { aolDataItemsRef.current = aolDataItems; }, [aolDataItems]);

    const updateOnHoursSync = async (noonReport: ValidatedNoonReport) => {
        const noonReportId = noonReport.metadata.noonReportId;
        const noonReportGuid = noonReport.metadata.noonReportGuid;

        if (!selectedAolDataItem || !noonReports || !aolItems || !selectedVessel) return;

        const matchByGuid = noonReportGuid
            ? (n: ValidatedNoonReport) => n.metadata.noonReportGuid === noonReportGuid
            : (n: ValidatedNoonReport) => n.metadata.noonReportId === noonReportId;
        const indexOfNoonReport = noonReports.findIndex(matchByGuid);
        noonReports[indexOfNoonReport] = noonReport;
        const _aolDataItems = await makeAolDataItemList(aolItems, noonReports, isFeatureEnabled, selectedVessel, loadedMasterStowagePlan);
        const _aolGroups = makeAolGroupList(_aolDataItems, selectedVessel);
        const _aolDataItem = _aolDataItems.find(
            (a) =>
                (noonReportGuid && a.noonReport?.metadata.noonReportGuid === noonReportGuid) || a.noonReport?.metadata.noonReportId === noonReportId
        );

        setAolDataItems([..._aolDataItems]);
        setAolGroups([..._aolGroups]);

        if (_aolDataItem) {
            setSelectedAolDataItem({ ..._aolDataItem });
        }

        if (selectedAolDataItem.noonReport && noonReport.metadata.noonReportId === selectedAolDataItem.noonReport.metadata.noonReportId) {
            setAolSyncEvent({ type: 'sync_hours', noonReport });
        }
    };
    const updateOnImosPortCallIdSync = async (noonReportId: number, imosPortCallId: number, noonReportGuid?: string) => {
        if (!selectedAolDataItem || !noonReports || !aolItems || !selectedVessel) return;

        const indexOfNoonReport = noonReports.findIndex(
            (n) => (noonReportGuid && n.metadata.noonReportGuid === noonReportGuid) || n.metadata.noonReportId === noonReportId
        );
        const noonReport = noonReports[indexOfNoonReport];

        if (!noonReport) return;
        noonReport.metadata.imosPortCallId = imosPortCallId;

        noonReports[indexOfNoonReport] = noonReport;
        const _aolDataItems = await makeAolDataItemList(aolItems, noonReports, isFeatureEnabled, selectedVessel, loadedMasterStowagePlan);
        const _aolGroups = makeAolGroupList(_aolDataItems, selectedVessel);
        const _aolDataItem = _aolDataItems.find(
            (a) =>
                (noonReportGuid && a.noonReport?.metadata.noonReportGuid === noonReportGuid) || a.noonReport?.metadata.noonReportId === noonReportId
        );

        setAolDataItems([..._aolDataItems]);
        setAolGroups([..._aolGroups]);

        if (_aolDataItem) {
            setSelectedAolDataItem({ ..._aolDataItem });
        }
    };

    const updateOnFuelRobSync = (fuelRobSection: NoonReportFuelRobSection) => {
        if (!fuelRobSection || !aolItems || !selectedVessel) return;
        const noonReportId = fuelRobSection.sectionData.noonReportId;

        const indexOfNoonReport = noonReports.findIndex((n) => n.metadata.noonReportId === noonReportId);
        const noonReport = noonReports[indexOfNoonReport];

        if (!noonReport) return;

        console.log('updateOnFuelRobSync: Updating noonReports[indexOfNoonReport].fuelRobSection...');
        noonReport.fuelRobSection = fuelRobSection;
        selectedAolDataItem.noonReport = noonReport;
        if (selectedAolDataItem.noonReport.metadata.noonReportId === noonReportId) {
            console.log('updateOnFuelRobSync: setting AolDataItems...');
            setSelectedAolDataItem({ ...selectedAolDataItem });
        }
    };

    const updateAolList = (dto: AolSectionSignalRDTO, addNoonReportDTO?: { aolDataItems: AolDataItem[]; aolGroups: AolGroup[] }) => {
        const noonReportId = +dto.noonReportId;
        const noonReportGuid = dto.noonReportGuid;
        const _aolDataItems = addNoonReportDTO?.aolDataItems ?? aolDataItemsRef.current;
        const _aolGroups = addNoonReportDTO?.aolGroups ?? aolGroupsRef.current;

        const matchesReport = (a: AolDataItem) =>
            a.noonReport &&
            ((noonReportGuid && a.noonReport.metadata.noonReportGuid === noonReportGuid) || a.noonReport.metadata.noonReportId === noonReportId);

        const aolGroup = _aolGroups.find((g) => g.aolDataItems.find(matchesReport));
        const aolDataItemIndex = _aolDataItems.findIndex(matchesReport);
        const aolDataItem = _aolDataItems[aolDataItemIndex];

        if (!aolGroup || !aolDataItem) return;

        setSection(aolDataItem, dto.section);

        // Update metadata (lastUpdatedUtc, lastUpdatedBy) if provided in the DTO
        if (dto.metadata && aolDataItem.noonReport) {
            const updatedMetadata = noonReportMetadataFactory.create(dto.metadata);
            aolDataItem.noonReport.metadata.lastUpdatedUtc = updatedMetadata.lastUpdatedUtc;
            aolDataItem.noonReport.metadata.lastUpdatedBy = updatedMetadata.lastUpdatedBy;
        }

        addAolDataItemToAolGroup(aolDataItem, aolGroup);

        // Defensive recomputation: ensure group status stays consistent after section
        // data updates, especially from fire-and-forget getUpdatedSection calls.
        const recomputedStatus = getAolGroupStatus(aolGroup.aolDataItems);
        if (recomputedStatus !== aolGroup.status) {
            aolGroup.status = recomputedStatus;
            setIsUpdatingAolGroupStatus(true);
        }

        _aolDataItems[aolDataItemIndex] = aolDataItem;
        setAolGroups([..._aolGroups]);
        setAolDataItems([..._aolDataItems]);

        if (!selectedAolDataItem) return;

        if (aolDataItem) {
            setSelectedAolDataItem({ ...aolDataItem });
        }

        if (
            aolDataItem.noonReport &&
            selectedAolDataItem.noonReport &&
            aolDataItem.noonReport.metadata.noonReportId === selectedAolDataItem.noonReport.metadata.noonReportId
        ) {
            const section = dto.section as any;
            setSaveAolEvent({
                type: 'save_section_done',
                section: section.sectionData.section,
                imoNumber: dto.imoNumber,
                aolDataItem,
            });
        }
    };

    const addNoonReport = async (noonReport: ValidatedNoonReport) => {
        if (!noonReports || !aolItems || !selectedVessel) return;
        const noonReportExists = noonReports.some(
            (n) =>
                (noonReport.metadata.noonReportGuid && n.metadata.noonReportGuid === noonReport.metadata.noonReportGuid) ||
                n.metadata.noonReportId === noonReport.metadata.noonReportId
        );
        if (noonReportExists) return;

        noonReports.push(noonReport);
        noonReports.sort((a, b) =>
            isDateSameOrBefore(
                a.generalSection.noonReportDateTime.reportFromTimeLocalTime,
                b.generalSection.noonReportDateTime.reportFromTimeLocalTime,
                'minute'
            )
                ? -1
                : 1
        );
        setNoonReports([...noonReports]);
        const aolDataItems = await makeAolDataItemList(aolItems, noonReports, isFeatureEnabled, selectedVessel, loadedMasterStowagePlan);
        const aolGroups = makeAolGroupList(aolDataItems, selectedVessel);

        setAolDataItems([...aolDataItems]);
        setAolGroups([...aolGroups]);

        return { aolDataItems, aolGroups };
    }; // TODO: PERFORMANCE - refactor and merge updateAolStatusToOtherUsers & refresh updateAolStatusToUpdater
    const updateAolStatusToUpdater = (dto: AolSectionDataUpdateDto) => {
        const imoNumber = dto.imoNumber;

        const matchNoonReport = (a: AolDataItem) => a.noonReport && dto.noonReportGuid && a.noonReport.metadata.noonReportGuid === dto.noonReportGuid;
        const aolGroup = aolGroups.find((g) => g.aolDataItems.find(matchNoonReport));
        const aolDataItem = aolGroup?.aolDataItems.find(matchNoonReport);

        if (!aolGroup || !aolDataItem?.noonReport) return;

        updateSectionStatus(aolDataItem, dto);

        const isAolDataItemStatusUpdated = checkAolDataItemUpdateStatus(aolDataItem, dto);
        updateAolDataItemStatus(aolDataItem, dto);

        updateAolGroup(aolGroup, aolDataItem, dto.noonReportGuid);

        if (isAolDataItemStatusUpdated) {
            const newGroupStatus = getAolGroupStatus(aolGroup.aolDataItems);

            const isAolGroupStatusChanged = isAolGroupStatusUpdated(newGroupStatus, aolGroup);
            if (isAolGroupStatusChanged) {
                const updateGroupMessage = getUpdateGroupMessage(newGroupStatus, aolGroup, '');
                setAolGroupStatusMessage(updateGroupMessage);

                updatePreviousAolListItem(aolGroup);

                aolGroup.status = newGroupStatus;
                setIsUpdatingAolGroupStatus(true);

                if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
                    updateAolMissingTasks(imoNumber);
                }
            }
        }

        setAolGroups([...aolGroups]);
    };

    const updateAolStatusToOtherUsers = (dto: AolSectionDataUpdateDto, senderName: string) => {
        const currentAolGroups = aolGroupsRef.current;
        const matchNoonReport = (a: AolDataItem) => a.noonReport && dto.noonReportGuid && a.noonReport.metadata.noonReportGuid === dto.noonReportGuid;
        const aolGroup = currentAolGroups.find((g) => g.aolDataItems.find(matchNoonReport));
        const aolDataItem = aolGroup?.aolDataItems.find(matchNoonReport);

        if (!aolGroup || !aolDataItem?.noonReport) return;

        // Guard against out-of-order messages: if this item is already marked as AOL approved
        // and the incoming DTO says a section is approved but AOL is not yet approved, it's a
        // stale intermediate message from an earlier save that completed after the final one.
        if (aolDataItem.noonReport.metadata.isAolApproved && !dto.isAolApproved && dto.isSectionApproved) {
            return;
        }

        updateSectionStatus(aolDataItem, dto);

        const isAolDataItemStatusUpdated = checkAolDataItemUpdateStatus(aolDataItem, dto);
        updateAolDataItemStatus(aolDataItem, dto);

        updateAolGroup(aolGroup, aolDataItem, dto.noonReportGuid);

        if (isAolDataItemStatusUpdated) {
            const newGroupStatus = getAolGroupStatus(aolGroup.aolDataItems);

            const isAolGroupStatusChanged = isAolGroupStatusUpdated(newGroupStatus, aolGroup);
            if (isAolGroupStatusChanged) {
                const isSelectedAolGroupChanged = isSelectedAolGroupUpdated(aolGroup);

                if (isSelectedAolGroupChanged) {
                    const updateGroupMessage = getUpdateGroupMessage(newGroupStatus, aolGroup, senderName);
                    setAolGroupStatusMessage(updateGroupMessage);
                    updatePreviousAolListItem(aolGroup);
                } else {
                    setPreviousAolGroup(null);
                }

                aolGroup.status = newGroupStatus;
                setIsUpdatingAolGroupStatus(true);
            }
        }
        setAolGroups([...currentAolGroups]);
    };

    const updateAolDataItemStatus = (aolDataItem: AolDataItem, dto: AolSectionDataUpdateDto) => {
        aolDataItem.status = dto.isAolApproved
            ? NoonReportStatusEnum.Approved
            : dto.isAolCompleted
            ? NoonReportStatusEnum.Completed
            : NoonReportStatusEnum.Edited;

        aolDataItem.noonReport!.metadata.isAolComplete = dto.isAolCompleted;
        aolDataItem.noonReport!.metadata.isAolApproved = dto.isAolApproved;
    };

    const updateAolGroup = (aolGroup: AolGroup, aolDataItem: AolDataItem, noonReportGuid?: string) => {
        const dataItemIndex = aolGroup.aolDataItems.findIndex(
            (a) => a.noonReport && noonReportGuid && a.noonReport.metadata.noonReportGuid === noonReportGuid
        );
        if (dataItemIndex >= 0) {
            aolGroup.aolDataItems[dataItemIndex] = aolDataItem;
        }
    };

    const updatePreviousAolListItem = (aolGroup: AolGroup) => {
        const previousAolGroup = findPreviousAolGroup(aolGroup);

        if (previousAolGroup) {
            setPreviousAolGroup(previousAolGroup);
        }
    };

    const isAolGroupStatusUpdated = (newGroupStatus: NoonReportStatusEnum, aolGroup: AolGroup) => {
        return newGroupStatus !== aolGroup.status;
    };

    const checkAolDataItemUpdateStatus = (aolDataItem: AolDataItem, dto: AolSectionDataUpdateDto) => {
        let isAolStatusUpdated = false;
        if (
            aolDataItem.noonReport?.metadata.isAolComplete !== dto.isAolCompleted ||
            aolDataItem.noonReport?.metadata.isAolApproved !== dto.isAolApproved
        ) {
            isAolStatusUpdated = true;
        }

        return isAolStatusUpdated;
    };

    const findPreviousAolGroup = (aolGroup: AolGroup) => {
        let filteredAolGroups;
        if (aolGroup.status === NoonReportStatusEnum.Completed && activeAolPage === AolPageEnum.Approval) {
            filteredAolGroups = aolGroups.filter((a) => aolFilterNoonReportStatus('approval', a.status, aolGroup.aolDate, aolFilter.fromDate));
        } else if (aolGroup.status === NoonReportStatusEnum.Approved && activeAolPage === AolPageEnum.Approved) {
            filteredAolGroups = aolGroups.filter((a) => aolFilterNoonReportStatus('approved', a.status, aolGroup.aolDate, aolFilter.fromDate));
        } else if (activeAolPage === AolPageEnum.Reporting) {
            filteredAolGroups = aolGroups.filter((a) => aolFilterNoonReportStatus('reporting', a.status, aolGroup.aolDate, aolFilter.fromDate));
        }

        const groupIndex = filteredAolGroups.findIndex((a) => isDateSame(a.aolDate, getAolGroupDate(aolGroup), 'day'));
        return filteredAolGroups[groupIndex + 1];
    };

    const getUpdateGroupMessage = (newGroupStatus: NoonReportStatusEnum, aolGroup: AolGroup, senderName: string) => {
        let updateMessage = '';
        let startMessage;
        if (senderName) {
            startMessage = senderName + ' has edited AOL. Updating status';
        } else {
            startMessage = 'Updating status';
        }

        if (newGroupStatus === NoonReportStatusEnum.Edited && aolGroup.status === NoonReportStatusEnum.Completed) {
            updateMessage = startMessage + '\nAOL moved to Report AOL';
        } else if (newGroupStatus === NoonReportStatusEnum.Edited && aolGroup.status === NoonReportStatusEnum.Approved) {
            updateMessage = startMessage + '\nAOL moved to Report AOL';
        } else if (newGroupStatus === NoonReportStatusEnum.Completed && aolGroup.status === NoonReportStatusEnum.Edited) {
            updateMessage = startMessage + '\nAOL moved to Approval';
        } else if (newGroupStatus === NoonReportStatusEnum.Approved && aolGroup.status === NoonReportStatusEnum.Completed) {
            updateMessage = startMessage + '\nAOL approved';
        }

        return updateMessage;
    };

    const isSelectedAolGroupUpdated = (aolGroup: AolGroup) => {
        if (!selectedAolGroup || !aolGroup) return false;
        if (isDateSame(getAolGroupDate(selectedAolGroup), getAolGroupDate(aolGroup), 'day')) {
            return true;
        }
        return false;
    };

    const updateSectionStatus = (aolDataItem: AolDataItem, dto: AolSectionDataUpdateDto) => {
        const noonReport = aolDataItem.noonReport;
        if (!noonReport) return;

        const section = GetSection(dto.noonReportSectionEnum, noonReport);
        if (!section || !section.sectionData) return;
        const sectionData = section.sectionData;
        sectionData.isCompleted = dto.isSectionCompleted;
        sectionData.isApproved = dto.isSectionApproved;
    };

    const addAolDataItemToAolGroup = (aolDataItem: AolDataItem, aolGroup: AolGroup) => {
        const dataItemIndex = aolGroup.aolDataItems.findIndex(
            (a) =>
                isDateSame(a.reportFromTimeLocalTime, aolDataItem.reportFromTimeLocalTime, 'minute') &&
                isDateSame(a.reportToTimeLocalTime, aolDataItem.reportToTimeLocalTime, 'minute')
        );
        aolGroup.aolDataItems[dataItemIndex] = aolDataItem;

        const groupIndex = aolGroups.findIndex((a) => isDateSame(getAolGroupDate(a), getAolGroupDate(aolGroup), 'day'));
        aolGroups[groupIndex] = aolGroup;
    };

    const updateOnDelete = async (noonReportIdStr: string | undefined, noonReportGuid?: string) => {
        const noonReportId = noonReportIdStr ? +noonReportIdStr : 0;
        if (!noonReports || !aolItems || !selectedVessel) return;

        const noonReportIndex = noonReports.findIndex(
            (n) => (noonReportGuid && n.metadata.noonReportGuid === noonReportGuid) || (!noonReportGuid && n.metadata.noonReportId === noonReportId)
        );

        if (noonReportIndex > -1) {
            noonReports.splice(noonReportIndex, 1);
            setNoonReports([...noonReports]);

            const aolDataItems = await makeAolDataItemList(aolItems, noonReports, isFeatureEnabled, selectedVessel, loadedMasterStowagePlan);
            const aolGroups = makeAolGroupList(aolDataItems, selectedVessel);

            setAolDataItems([...aolDataItems]);
            setAolGroups([...aolGroups]);
        }
    };

    const toggleToast = (updatedNoonReportId: number, signalRResponse: any, hubConnection: HubConnection) => {
        const noonReportId = selectedAolDataItem?.noonReport?.metadata.noonReportId;
        const updatedAolItem = aolDataItems.find((a) => a.noonReport?.metadata.noonReportId === updatedNoonReportId);

        const showToast =
            (!!noonReportId && noonReportId === updatedNoonReportId) ||
            (updatedAolItem && isDateSame(selectedBunkeringDate, updatedAolItem.reportDate, 'day'));

        if (showToast && signalRResponse.connectionId !== hubConnection.connectionId) {
            if (signalRResponse.sender) {
                toastSuccess(signalRResponse.sender + ' has updated the AOL');
            } else {
                toastSuccess('The AOL has been updated');
            }
        }
    };

    const reconcileGroupStatuses = () => {
        const currentGroups = aolGroupsRef.current;
        if (!currentGroups || currentGroups.length === 0) return;

        let hasChanges = false;
        for (const group of currentGroups) {
            const computedStatus = getAolGroupStatus(group.aolDataItems);
            if (computedStatus !== group.status) {
                group.status = computedStatus;
                hasChanges = true;
            }
        }

        if (hasChanges) {
            setAolGroups([...currentGroups]);
            setIsUpdatingAolGroupStatus(true);
        }
    };

    return {
        updateAolList,
        updateAolStatusToUpdater,
        updateAolStatusToOtherUsers,
        updateOnDelete,
        addNoonReport,
        toggleToast,
        updateOnHoursSync,
        updateOnImosPortCallIdSync,
        updateOnFuelRobSync,
        reconcileGroupStatuses,
    };
};
